import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  MessageCircle, 
  Phone, 
  Mail, 
  Clock, 
  Send, 
  Shield, 
  Zap, 
  MapPin,
  Building2,
  ShoppingBag,
  Users,
  Headphones
} from 'lucide-react';
import MapSection from '@/components/MapSection';
import { useLanguage } from '@/context/LanguageContext';

// Contact Data
const CONTACTS = {
  mainLandline: '+97167796889',
  mainLandlineDisplay: '+971 6 779 6889',
  shopping: '+971551073662',
  shoppingDisplay: '+971 55 107 3662',
  management1: '+971502951774',
  management1Display: '+971 50 295 1774',
  management2: '+971502951773',
  management2Display: '+971 50 295 1773',
  email: 'info@tadaber-almostqbl.com',
};

const ContactPage = () => {
  const { t, isRTL } = useLanguage();
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Trust Badges with translations
  const trustBadges = [
    { icon: Zap, label: t('contact.fastResponse'), color: 'text-uae-green' },
    { icon: MapPin, label: t('contact.uaeSupport'), color: 'text-uae-red' },
    { icon: Shield, label: t('contact.secureProf'), color: 'text-accent' },
  ];

  // Services for dropdown with translations
  const services = [
    t('services.nannies.title'),
    t('services.housemaids.title'),
    t('services.security.title'),
    t('services.cooks.title'),
    t('services.drivers.title'),
    t('services.teachers.title'),
    t('services.caregivers.title'),
    t('services.farmers.title'),
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 1500));

    toast.success(t('contact.messageSent'), {
      description: t('contact.messageSuccess'),
    });

    // Reset form
    setFormData({
      name: '',
      phone: '',
      service: '',
      message: '',
    });
    setIsSubmitting(false);
  };

  const openWhatsApp = (number, dept = '') => {
    const message = encodeURIComponent(
      `Hello! I'm contacting ${dept ? dept + ' - ' : ''}TDABYR ALMSTQBL. I'm interested in your staffing services.`
    );
    window.open(`https://wa.me/${number.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
  };

  const openCall = (number) => {
    window.open(`tel:${number.replace(/[^0-9+]/g, '')}`, '_self');
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Header Section */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-card to-background relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-uae-red/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-uae-green/5 rounded-full blur-3xl" />
        
        <div className="container-custom relative z-10">
          <div className={`text-center max-w-3xl mx-auto ${isRTL ? 'text-right' : ''}`}>
            {/* Title */}
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-4">
              {t('contact.pageTitle')} <span className="text-gradient-uae">{t('contact.pageTitleHighlight')}</span>
            </h1>
            
            {/* Subtitle */}
            <p className="text-lg md:text-xl text-muted-foreground mb-8">
              {t('contact.pageSubtitle')}
            </p>
            
            {/* Trust Badges */}
            <div className={`flex flex-wrap items-center justify-center gap-4 md:gap-8 ${isRTL ? 'flex-row-reverse' : ''}`}>
              {trustBadges.map((badge) => (
                <div 
                  key={badge.label}
                  className={`flex items-center gap-2 px-4 py-2 bg-muted/50 rounded-full border border-border/50 ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  <badge.icon className={`w-4 h-4 ${badge.color}`} />
                  <span className="text-sm font-medium text-foreground">{badge.label}</span>
                </div>
              ))}
            </div>
            
            {/* UAE Flag Accent */}
            <div className="flex justify-center gap-2 mt-8">
              <div className="w-12 h-1.5 bg-uae-red rounded-full" />
              <div className="w-12 h-1.5 bg-uae-green rounded-full" />
              <div className="w-12 h-1.5 bg-foreground rounded-full" />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Cards Section */}
      <section className="py-12 md:py-16">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            
            {/* Card A: Main Landline */}
            <Card className="bg-card border-border group hover:border-uae-green/50 transition-all duration-300 hover:shadow-lg hover:shadow-uae-green/10 hover:-translate-y-1">
              <CardHeader className="pb-4">
                <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="w-14 h-14 rounded-xl bg-uae-green/10 flex items-center justify-center group-hover:bg-uae-green/20 transition-colors">
                    <Building2 className="w-7 h-7 text-uae-green" />
                  </div>
                  <div className={isRTL ? 'text-right' : ''}>
                    <CardTitle className="font-display text-xl text-foreground">{t('contact.mainOffice')}</CardTitle>
                    <CardDescription className="text-muted-foreground">{t('contact.landline')}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className={`space-y-4 ${isRTL ? 'text-right' : ''}`}>
                <p className="text-2xl font-semibold text-foreground tracking-wide" dir="ltr">
                  {CONTACTS.mainLandlineDisplay}
                </p>
                <div className={`flex flex-col sm:flex-row gap-3 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                  <Button 
                    variant="uaeGreen" 
                    className="flex-1 gap-2"
                    onClick={() => openWhatsApp(CONTACTS.mainLandline, 'Main Office')}
                  >
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp
                  </Button>
                  <Button 
                    variant="outline" 
                    className="flex-1 gap-2 border-muted-foreground/30 hover:bg-muted"
                    onClick={() => openCall(CONTACTS.mainLandline)}
                  >
                    <Phone className="w-4 h-4" />
                    {t('services.callUs')}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Card B: Shopping Department */}
            <Card className="bg-card border-border group hover:border-uae-red/50 transition-all duration-300 hover:shadow-lg hover:shadow-uae-red/10 hover:-translate-y-1">
              <CardHeader className="pb-4">
                <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="w-14 h-14 rounded-xl bg-uae-red/10 flex items-center justify-center group-hover:bg-uae-red/20 transition-colors">
                    <ShoppingBag className="w-7 h-7 text-uae-red" />
                  </div>
                  <div className={isRTL ? 'text-right' : ''}>
                    <CardTitle className="font-display text-xl text-foreground">{t('contact.shoppingDept')}</CardTitle>
                    <CardDescription className="text-muted-foreground">{t('contact.salesInquiries')}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className={`space-y-4 ${isRTL ? 'text-right' : ''}`}>
                <p className="text-2xl font-semibold text-foreground tracking-wide" dir="ltr">
                  {CONTACTS.shoppingDisplay}
                </p>
                <div className={`flex flex-col sm:flex-row gap-3 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                  <Button 
                    variant="uaeGreen" 
                    className="flex-1 gap-2"
                    onClick={() => openWhatsApp(CONTACTS.shopping, 'Shopping Dept')}
                  >
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp
                  </Button>
                  <Button 
                    variant="outline" 
                    className="flex-1 gap-2 border-muted-foreground/30 hover:bg-muted"
                    onClick={() => openCall(CONTACTS.shopping)}
                  >
                    <Phone className="w-4 h-4" />
                    {t('services.callUs')}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Card C: Management */}
            <Card className="bg-card border-border group hover:border-accent/50 transition-all duration-300 hover:shadow-lg hover:shadow-accent/10 hover:-translate-y-1">
              <CardHeader className="pb-4">
                <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                    <Users className="w-7 h-7 text-accent" />
                  </div>
                  <div className={isRTL ? 'text-right' : ''}>
                    <CardTitle className="font-display text-xl text-foreground">{t('contact.management')}</CardTitle>
                    <CardDescription className="text-muted-foreground">{t('contact.directLines')}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Management Number 1 */}
                <div className={`flex items-center justify-between p-3 bg-muted/30 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span className="text-lg font-medium text-foreground" dir="ltr">{CONTACTS.management1Display}</span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="gap-2 border-muted-foreground/30 hover:bg-muted"
                    onClick={() => openCall(CONTACTS.management1)}
                  >
                    <Phone className="w-4 h-4" />
                    {t('services.callUs')}
                  </Button>
                </div>
                {/* Management Number 2 */}
                <div className={`flex items-center justify-between p-3 bg-muted/30 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span className="text-lg font-medium text-foreground" dir="ltr">{CONTACTS.management2Display}</span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="gap-2 border-muted-foreground/30 hover:bg-muted"
                    onClick={() => openCall(CONTACTS.management2)}
                  >
                    <Phone className="w-4 h-4" />
                    {t('services.callUs')}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Card D: Email */}
            <Card className="bg-card border-border group hover:border-uae-green/50 transition-all duration-300 hover:shadow-lg hover:shadow-uae-green/10 hover:-translate-y-1">
              <CardHeader className="pb-4">
                <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="w-14 h-14 rounded-xl bg-uae-green/10 flex items-center justify-center group-hover:bg-uae-green/20 transition-colors">
                    <Mail className="w-7 h-7 text-uae-green" />
                  </div>
                  <div className={isRTL ? 'text-right' : ''}>
                    <CardTitle className="font-display text-xl text-foreground">{t('contact.emailUs')}</CardTitle>
                    <CardDescription className="text-muted-foreground">{t('contact.generalInquiries')}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className={`space-y-4 ${isRTL ? 'text-right' : ''}`}>
                <p className="text-lg font-medium text-foreground break-all" dir="ltr">
                  {CONTACTS.email}
                </p>
                <Button 
                  variant="uaeGreen" 
                  className="w-full gap-2"
                  onClick={() => window.open(`mailto:${CONTACTS.email}`, '_self')}
                >
                  <Mail className="w-4 h-4" />
                  {t('contact.sendEmail')}
                </Button>
                
                {/* Working Hours */}
                <div className="pt-4 border-t border-border/50">
                  <div className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">{t('contact.workingHours')}</span>
                  </div>
                  <div className={`flex items-center gap-2 text-muted-foreground mt-1 ${isRTL ? 'mr-6' : 'ml-6'}`}>
                    <span className="text-sm text-uae-red">{t('contact.closedDay')}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-12 md:py-16 bg-card">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <Card className="bg-background border-border shadow-premium">
              <CardHeader className={`text-center pb-6 ${isRTL ? 'text-right' : ''}`}>
                <div className={`w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-uae-red to-uae-green flex items-center justify-center mb-4 ${isRTL ? 'mx-0 mr-auto ml-auto' : ''}`}>
                  <Headphones className="w-8 h-8 text-uae-white" />
                </div>
                <CardTitle className="font-display text-2xl md:text-3xl text-foreground">
                  {t('contact.sendMessage')}
                </CardTitle>
                <CardDescription className="text-muted-foreground text-base">
                  {t('contact.formSubtitle')}
                </CardDescription>
              </CardHeader>
              
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Service Selection */}
                  <div className={`space-y-2 ${isRTL ? 'text-right' : ''}`}>
                    <Label htmlFor="service" className="text-foreground">{t('contact.serviceRequired')} *</Label>
                    <Select value={formData.service} onValueChange={handleServiceChange} required>
                      <SelectTrigger className="bg-muted border-border h-12">
                        <SelectValue placeholder={t('contact.selectService')} />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem key={service} value={service}>
                            {service}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Name & Phone Row */}
                  <div className={`grid sm:grid-cols-2 gap-4 ${isRTL ? 'direction-rtl' : ''}`}>
                    <div className={`space-y-2 ${isRTL ? 'text-right' : ''}`}>
                      <Label htmlFor="name" className="text-foreground">{t('contact.fullName')} *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder={t('contact.yourName')}
                        required
                        className="bg-muted border-border h-12"
                      />
                    </div>
                    <div className={`space-y-2 ${isRTL ? 'text-right' : ''}`}>
                      <Label htmlFor="phone" className="text-foreground">{t('contact.phoneNumber')} *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+971 XX XXX XXXX"
                        required
                        className="bg-muted border-border h-12"
                        dir="ltr"
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div className={`space-y-2 ${isRTL ? 'text-right' : ''}`}>
                    <Label htmlFor="message" className="text-foreground">{t('contact.yourMessage')}</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder={t('contact.messagePlaceholder')}
                      rows={4}
                      className="bg-muted border-border resize-none"
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('contact.replyNote')}
                    </p>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    variant="uaeRed"
                    size="lg"
                    className="w-full gap-2 h-14 text-base"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-uae-white/30 border-t-uae-white rounded-full animate-spin" />
                        {t('contact.sending')}
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {t('contact.send')}
                      </>
                    )}
                  </Button>
                </form>

                {/* Security Note */}
                <div className={`flex items-center justify-center gap-2 mt-6 pt-6 border-t border-border/50 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Shield className="w-4 h-4 text-uae-green" />
                  <span className="text-xs text-muted-foreground">
                    {t('contact.secureNote')}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Google Maps Section */}
      <MapSection className="bg-card" />

      {/* Quick WhatsApp CTA */}
      <section className="py-12 bg-gradient-to-r from-uae-red to-uae-green">
        <div className={`container-custom text-center ${isRTL ? 'text-right' : ''}`}>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-uae-white mb-3">
            {t('contact.needImmediate')}
          </h2>
          <p className="text-uae-white/80 mb-6 max-w-xl mx-auto">
            {t('contact.needImmediateSubtitle')}
          </p>
          <Button
            variant="default"
            size="xl"
            className="bg-uae-white text-uae-black hover:bg-uae-white/90 gap-2 shadow-lg"
            onClick={() => openWhatsApp(CONTACTS.shopping, '')}
          >
            <MessageCircle className="w-5 h-5" />
            {t('contact.chatNow')}
          </Button>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
